"use client";

import type { ComponentProps, ReactNode } from "react";
import type { FontAwesomeIcon, FontAwesomeTheme } from "@/shared/components/ui/icon/fa.types";

import cn from "classnames";

import { useFontAwesome } from "@/shared/components/ui/icon/useFontAwesome";

export interface FontAwesomeIconProps extends Omit<ComponentProps<"span">, "prefix"> {
  iconClassName?: string;
  theme?: FontAwesomeTheme;
  icon: FontAwesomeIcon;
  color?: string;
  text?: ReactNode;
  prefix?: ReactNode;
  spin?: boolean;
}

/**
 * FontAwesome Icon component (v7)
 *
 * @example
 * <FA icon="fa-user" />
 * <FA icon="fa-star" theme="fas" />     // Solid
 * <FA icon="fa-star" theme="fal" />     // Light (default)
 * <FA icon="fa-star" theme="fad" />     // Duotone
 * <FA icon="fa-download" text="Download" />
 *
 * @see https://fontawesome.com/v7/icons
 */
export const FA = ({
  className,
  iconClassName,
  theme = "far",
  icon,
  color,
  onClick,
  style,
  spin,
  prefix,
  text,
  ...props
}: FontAwesomeIconProps) => {
  useFontAwesome();

  return (
    <span
      data-icon={icon}
      className={cn(className, !!onClick && "cursor-pointer")}
      style={style}
      onClick={onClick}
      {...props}
    >
      {prefix}{" "}
      <i
        style={{ color: color?.startsWith("#") ? color : color ? `var(--color-${color})` : "" }}
        className={cn(theme, spin && "fa-spin", icon, "icon", iconClassName)}
      />
      {!!text && <span style={{ marginInlineStart: "0.5rem" }}>{text}</span>}
    </span>
  );
};
